import { isIos } from './pwa';

// Push notifications (docs/pwa.md): news from the team, only in the installed app (owner decision). What this device can
// do and the plumbing the browser's Push API needs. Nothing is ever asked on page load: the browser's question comes
// only from a tap on the sheet's own "Ativar" (a refused permission can never be asked again).

export const PUSH_CONFIG_PATH = '/api/push/config';
export const PUSH_SUBSCRIPTION_PATH = '/api/push/subscription';
export const PUSH_UNSUBSCRIBE_PATH = '/api/push/unsubscribe';

/** When this device last told the server its subscription (re-sent at most once a day). */
export const PUSH_SYNCED_KEY = 'mt-push-synced-at';
export const PUSH_SYNC_EVERY_MS = 24 * 60 * 60 * 1000;
/** The installed app offered the sheet by itself on this device (once, ever: "Agora não" is final there). */
export const PUSH_APP_ASKED_KEY = 'mt-push-app-asked-at';

const browserStorage = () => (typeof localStorage === 'undefined' ? null : localStorage);
const apiOrigin = () => String(import.meta.env.VITE_GRAPHQL_URL || '').replace(/\/graphql$/, '');

/** `ok` with the Push API and a service worker; `unsupported` otherwise (the Push API exists on iOS only in the installed app). */
export const pushSupportOf = (win) => {
  if (!win) return 'unsupported';
  const hasApi = 'serviceWorker' in win.navigator && typeof win.PushManager !== 'undefined' && typeof win.Notification !== 'undefined';
  return hasApi ? 'ok' : 'unsupported';
};

/** The browser's permission ('default' = not asked yet); 'denied' can only be changed in the device's settings. */
export const permissionOf = (win) => {
  const permission = win?.Notification?.permission;
  return permission === 'granted' || permission === 'denied' ? permission : 'default';
};

/** The VAPID public key (base64url) as the bytes PushManager.subscribe wants. */
export const urlBase64ToUint8Array = (base64) => {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) out[index] = raw.charCodeAt(index);
  return out;
};

/** A short name for the device, shown nowhere public ("Chrome no Android", "Safari on iPhone"). */
export const deviceLabel = (userAgent, maxTouchPoints = 0, locale = 'pt') => {
  const ua = userAgent;
  let os = 'Linux';
  if (/Android/i.test(ua)) os = 'Android';
  else if (isIos(ua, maxTouchPoints)) os = /iPad|Macintosh/.test(ua) ? 'iPad' : 'iPhone';
  else if (/Mac OS X/i.test(ua)) os = 'Mac';
  else if (/Windows/i.test(ua)) os = 'Windows';
  let browser = 'Safari';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/SamsungBrowser/.test(ua)) browser = 'Samsung Internet';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/CriOS|Chrome\//.test(ua)) browser = 'Chrome';
  return `${browser} ${locale === 'pt' ? 'no' : 'on'} ${os}`;
};

export const readSyncedAt = (storage = browserStorage()) => {
  try {
    const value = Number(storage?.getItem(PUSH_SYNCED_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

export const writeSyncedAt = (at, storage = browserStorage()) => {
  try {
    if (at === null) storage?.removeItem(PUSH_SYNCED_KEY);
    else storage?.setItem(PUSH_SYNCED_KEY, String(at));
  } catch {
    // A private window: the next load re-sends, which is harmless
  }
};

export const syncDue = (syncedAt, now) => syncedAt === null || now - syncedAt >= PUSH_SYNC_EVERY_MS;

export const readAppAsked = (storage = browserStorage()) => {
  try {
    return Boolean(storage?.getItem(PUSH_APP_ASKED_KEY));
  } catch {
    return true;
  }
};

export const writeAppAsked = (at, storage = browserStorage()) => {
  try {
    storage?.setItem(PUSH_APP_ASKED_KEY, String(at));
  } catch {
    // A private window: it may offer again, harmless
  }
};

/**
 * The installed app opens the sheet by itself once: signed in, the server on, this device able and not on yet, the
 * browser not asked yet (a refusal is final), no other window up, never offered on this device before. The browser's own
 * question still comes only from the sheet's "Ativar".
 */
export const offersInApp = ({ standalone, signedIn, enabled, support, deviceChecked, subscribed, permission, busy, asked }) =>
  standalone && signedIn && enabled && support === 'ok' && deviceChecked && !subscribed && permission === 'default' && !busy && !asked;

const pushRequest = async (path, token, { method = 'GET', body } = {}) => {
  const response = await fetch(`${apiOrigin()}${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json().catch(() => ({}));
};

export const fetchPushConfig = (token) => pushRequest(PUSH_CONFIG_PATH, token);

export const saveSubscription = (body, token) => pushRequest(PUSH_SUBSCRIPTION_PATH, token, { method: 'PUT', body });

export const removeSubscription = (endpoint, token) => pushRequest(PUSH_UNSUBSCRIBE_PATH, token, { method: 'POST', body: { endpoint } });

/** The body of PUT /api/push/subscription: only what the contract names, whatever else the browser's JSON carries. */
export const subscriptionBody = (subscription, { userAgent, maxTouchPoints, locale }) => {
  const { endpoint, keys } = subscription.toJSON();
  return { endpoint, keys: { p256dh: keys?.p256dh, auth: keys?.auth }, label: deviceLabel(userAgent, maxTouchPoints, locale), locale };
};

/** The service worker's registration; `waitMs` to give a first load the time to register it. */
export const workerRegistration = async (waitMs = 5000) => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  if (existing || waitMs <= 0) return existing ?? null;
  return Promise.race([navigator.serviceWorker.ready.catch(() => null), new Promise((resolve) => setTimeout(() => resolve(null), waitMs))]);
};

export const currentSubscription = async () => {
  const registration = await workerRegistration(0);
  return (await registration?.pushManager?.getSubscription().catch(() => null)) ?? null;
};

/**
 * Turns this device off: the server first (it needs the session), then the browser. A failure on the server only leaves
 * a row it drops on its next send. Logout calls it with the token it's about to remove.
 */
export const unsubscribeThisDevice = async (token) => {
  const subscription = await currentSubscription();
  if (subscription) {
    await removeSubscription(subscription.endpoint, token).catch(() => undefined);
    await subscription.unsubscribe().catch(() => undefined);
  }
  writeSyncedAt(null);
};
